"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FeedItem = exports.FeedItemViewMode = exports.feedItemTestIds = void 0;
const tslib_1 = require("tslib");
const jsx_runtime_1 = require("react/jsx-runtime");
const frontend_infra_localisation_1 = require("@forto-dev/frontend-infra-localisation");
const silhouette_core_1 = require("@freighthub/silhouette-core");
const lodash_es_1 = require("lodash-es");
const moment_1 = tslib_1.__importDefault(require("moment"));
const useDateFormatter_1 = require("../../../util/useDateFormatter");
const types_1 = require("../types");
const ActivityTitle_1 = require("./ActivityTitle");
const FeedIcon_1 = require("./FeedIcon");
const FeedItemLogo_1 = require("./FeedItemLogo");
const GenericActivity_1 = require("./GenericActivity");
exports.feedItemTestIds = {
    feedItem: 'activity_feed-item-root',
    logoWrapper: 'activity_feed-item-logo_wrapper',
    contentWrapper: 'activity_feed-item-content_wrapper',
};
var FeedItemViewMode;
(function (FeedItemViewMode) {
    FeedItemViewMode["Right"] = "right";
    FeedItemViewMode["Left"] = "left";
})(FeedItemViewMode || (exports.FeedItemViewMode = FeedItemViewMode = {}));
const ActivityWidthMap = {
    [types_1.ActivityType.DeviationInsight]: 564,
    [types_1.ActivityType.DeviationRiskInsight]: 564,
    [types_1.ActivityType.ReassuranceInsight]: 564,
    [types_1.ActivityType.UpdateInsight]: 564,
    [types_1.ActivityType.ShipmentUpdateFeedV1]: 410,
    [types_1.ActivityType.UpdateShipment]: 410,
};
const useStyles = (0, silhouette_core_1.makeStyles)((theme) => ({
    container: ({ type, viewMode }) => ({
        maxWidth: ActivityWidthMap[type] || '510px',
        ...(viewMode === FeedItemViewMode.Right
            ? {
                'margin-left': 'auto',
                'margin-right': 0,
            }
            : {}),
    }),
    logoWrapper: ({ viewMode }) => ({
        alignItems: 'center',
        justifyContent: 'center',
        ...(viewMode === FeedItemViewMode.Right ? { marginLeft: theme.spacing(3) } : { marginRight: theme.spacing(3) }),
    }),
    mainContent: ({ viewMode, isInternalActivity }) => {
        const arrowBg = isInternalActivity ? theme.palette.tag.warning : theme.palette.common.white;
        return {
            position: 'relative',
            padding: theme.spacing(0, 0, 3),
            '&::before': {
                content: '""',
                position: 'absolute',
                right: '100%',
                border: `10px solid ${theme.palette.common.white}`,
                top: 22,
                left: -11,
                borderColor: `${arrowBg} transparent transparent ${arrowBg}`,
                transformOrigin: '0 0',
                transform: 'rotate(-45deg)',
                ...(viewMode === FeedItemViewMode.Right
                    ? {
                        left: 'calc(100% + 11px)',
                        transform: 'rotate(135deg)',
                    }
                    : {}),
            },
        };
    },
    textContentWrapper: {
        position: 'relative',
        paddingLeft: 0,
        wordBreak: 'break-word',
        flex: 1,
    },
    paper: ({ isInternalActivity }) => ({
        paddingTop: theme.spacing(1.5),
        background: isInternalActivity ? theme.palette.tag.warning : theme.palette.common.white,
        borderRadius: 8,
    }),
    internalNotesHelper: {
        padding: theme.spacing(0, 1.5, 0.5),
    },
    feedTitle: {
        padding: theme.spacing(0, 1.5, 1.5),
    },
    activityContent: ({ isInternalActivity }) => ({
        ...(isInternalActivity ? { border: `2px solid ${theme.palette.tag.warning}` } : {}),
        background: theme.palette.common.white,
        borderRadius: '0 0 8px 8px',
    }),
}));
const changeOpsManagerActivities = [
    types_1.ActivityType.ChangeOperationManagerFeedV1,
    types_1.ActivityType.ChangeOriginOperationManagerFeedV1,
    types_1.ActivityType.ChangeAssignee,
];
const internalActivityHighlightTextMap = {
    [types_1.ActivityType.CommunicationFeedV1]: 'activity_feed-internal_note',
};
const FeedItem = ({ feed, loggedInUserId, isForCustomer }) => {
    const { formatTimeShort } = (0, useDateFormatter_1.useDateFormatter)();
    const { current, translate } = (0, frontend_infra_localisation_1.useI18n)();
    const { id } = feed?.activity?.actor || {};
    const isLoggedInUser = !!id && loggedInUserId === id;
    const viewMode = isLoggedInUser ? FeedItemViewMode.Right : FeedItemViewMode.Left;
    const isInternalActivity = feed.feedInfo.activityVisibility === 'Internal';
    const classes = useStyles({ type: feed.activity.type, viewMode, isInternalActivity });
    const createdToday = (0, moment_1.default)(feed?.activity?.createdAt).startOf('day').isSame((0, moment_1.default)().startOf('day'), 'days');
    const Icon = FeedIcon_1.FeedIconMap[feed.activity.customField?.['ruleID']] || FeedIcon_1.FeedIconMap[feed.activity.type];
    return ((0, jsx_runtime_1.jsxs)(silhouette_core_1.Grid, { container: true, direction: isLoggedInUser ? 'row-reverse' : 'row', wrap: "nowrap", "data-testid": `${exports.feedItemTestIds.feedItem}-${viewMode}`, className: classes.container, children: [(0, jsx_runtime_1.jsx)(silhouette_core_1.Grid, { "data-testid": exports.feedItemTestIds.logoWrapper, className: classes.logoWrapper, children: (0, jsx_runtime_1.jsx)(FeedItemLogo_1.FeedItemLogo, { userInfo: feed?.activity?.actor }) }), (0, jsx_runtime_1.jsx)(silhouette_core_1.Grid, { "data-testid": exports.feedItemTestIds.contentWrapper, xs: true, className: classes.mainContent, children: (0, jsx_runtime_1.jsxs)(silhouette_core_1.Box, { className: classes.paper, children: [isInternalActivity && ((0, jsx_runtime_1.jsx)(silhouette_core_1.Typography, { variant: "caption", color: "textSecondary", className: classes.internalNotesHelper, component: "div", children: translate(internalActivityHighlightTextMap[feed.activity.type] || 'activity_feed-system_update') })), (0, jsx_runtime_1.jsxs)(silhouette_core_1.Grid, { container: true, wrap: "nowrap", alignItems: "center", className: classes.feedTitle, children: [Icon && ((0, jsx_runtime_1.jsx)(FeedIcon_1.FeedIcon, { Icon: Icon, type: feed.activity.type, ruleID: feed.activity.customField?.['ruleID'] })), (0, jsx_runtime_1.jsx)(silhouette_core_1.Grid, { className: classes.textContentWrapper, "data-e2e-id": "activity-header", children: (0, jsx_runtime_1.jsx)(ActivityTitle_1.ActivityTitle, { activity: feed.activity, isLoggedInUser: isLoggedInUser }) }), (0, jsx_runtime_1.jsx)(silhouette_core_1.Grid, { children: (0, jsx_runtime_1.jsx)(silhouette_core_1.Typography, { variant: "caption", color: "textSecondary", children: createdToday
                                            ? (0, moment_1.default)(feed?.activity?.createdAt).locale(current).fromNow()
                                            : formatTimeShort(feed?.activity.createdAt) }) })] }), (0, jsx_runtime_1.jsx)(silhouette_core_1.Box, { pl: Icon && !isInternalActivity ? 5.5 : 0, pt: isInternalActivity && !(0, lodash_es_1.includes)(changeOpsManagerActivities, feed.activity.type) ? 2 : 0, className: classes.activityContent, children: (0, jsx_runtime_1.jsx)(GenericActivity_1.GenericActivity, { feed: feed, isForCustomer: isForCustomer }) })] }) })] }));
};
exports.FeedItem = FeedItem;
//# sourceMappingURL=FeedItem.js.map